import { t } from '../../components/list/listShared';

/**
 * Items-only vocabulary for the Items list: product types, the price in
 * effect today, sales attributes and the sort options the endpoint accepts.
 *
 * Everything visual (tokens, icons, pills, bottom sheet, formatters) lives in
 * the list kit (src/components/list) and is re-exported here so the Items
 * dialogs keep a single import.
 */
export * from '../../components/list/listShared';

/* ------------------------------------------------------------------ */
/* Product types                                                       */
/* ------------------------------------------------------------------ */

/**
 * The API's Product_Types enum. `searchType` on
 * /api/inventory/products/list-for-dashboard is matched against the enum KEY
 * (case-insensitive substring), so the value we send is the key itself.
 */
export const TYPE_OPTIONS: { value: string; type: number; label: string }[] = [
  { value: 'Stock_Item', type: 2, label: 'Stock item' },
  { value: 'Sale_Item', type: 3, label: 'Sale item' },
  { value: 'Asset', type: 4, label: 'Asset' },
];

export const typeLabel = (type: any, language: any): string => {
  const found = TYPE_OPTIONS.find(o => o.type === Number(type));
  if (!found) return '';
  return t(language, found.label, found.label);
};

export const STATUS_OPTIONS = ['Active', 'Inactive'];

/* ------------------------------------------------------------------ */
/* Pricing                                                             */
/* ------------------------------------------------------------------ */

/**
 * The price row in effect today: active, non-deleted, and whose validity
 * window covers now (open bounds count). Falls back to the newest active row
 * so a lapsed window still shows something rather than a blank.
 */
export const currentPriceRowOf = (product: any): any | null => {
  const rows = (product?.productPrices ?? []).filter(
    (p: any) => p?.isActive !== false && p?.isDeleted !== true
  );
  if (rows.length === 0) return null;
  const now = Date.now();
  const byNewest = (a: any, b: any) =>
    new Date(b.validFrom ?? 0).getTime() - new Date(a.validFrom ?? 0).getTime();
  const covering = rows
    .filter((p: any) => {
      const from = p.validFrom ? new Date(p.validFrom).getTime() : -Infinity;
      const to = p.validTo ? new Date(p.validTo).getTime() : Infinity;
      return from <= now && to >= now;
    })
    .sort(byNewest)[0];
  return covering ?? [...rows].sort(byNewest)[0];
};

/** The numeric price in effect today, or null when the item has never been priced. */
export const currentPriceOf = (product: any): number | null => {
  const row = currentPriceRowOf(product);
  const value = Number(row?.price);
  return Number.isFinite(value) ? value : null;
};

/* ------------------------------------------------------------------ */
/* Sales attributes                                                    */
/* ------------------------------------------------------------------ */

/**
 * Sales attribute names for a list row. list-for-dashboard maps
 * attribute_product_maps onto `attributes`; each row carries the joined
 * `attribute` record. The `attributeName` fallbacks keep the chips rendering
 * if the join shape changes.
 */
export const salesAttributesOf = (product: any): string[] =>
  ((product?.attributes ?? []) as any[])
    .map((a: any) => a?.attribute?.attributeName ?? a?.attributeName ?? a?.displayName ?? '')
    .map((name: any) => String(name).trim())
    .filter((name: string) => name !== '');

/* ------------------------------------------------------------------ */
/* Sorting                                                             */
/* ------------------------------------------------------------------ */

/**
 * The sortable keys list-for-dashboard accepts. Price is deliberately
 * absent: an item's price lives in a history table with validity windows, so
 * "sort by price" has no single well-defined value at the SQL level.
 */
export const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'updated', label: 'Last updated' },
  { value: 'created', label: 'Date created' },
  { value: 'name', label: 'Item name' },
  { value: 'sku', label: 'SKU' },
  { value: 'group', label: 'Group' },
  { value: 'type', label: 'Type' },
  { value: 'status', label: 'Status' },
];

/* ------------------------------------------------------------------ */
/* Filters                                                             */
/* ------------------------------------------------------------------ */

export type FilterKey = 'searchType' | 'searchStatus' | 'searchGroup' | 'searchTime';
